// Resilient writer for the install's `Metadata` table rows (system/platform,
// system/infra-params). Extracted from platform-stamp.ts so EVERY writer of that
// table climbs the same auth ladder: the cloud installer's managed identity holds
// Owner at subscription scope but no Storage Table data role, so an AAD-only
// writer 403s on a from-zero install.
//
// ── RECORDED EXEMPTION from the installer's heartbeat sweep ───────────────────
//
// Both ladders below end in a bounded self-grant backoff. Every other bounded wait
// the install performs now reports itself on the status blob (installer/lib.sh,
// heartbeat_*) so that waiting and dead stop looking alike. THESE TWO CANNOT: they
// run inside the CLI process, which has no STATUS_FILE, no storage key, and no
// business acquiring either. The exemption is written down rather than left as
// silence, with its measurement:
//
//   ceiling      6 × 10s = 60s per ladder, stated by the loops themselves
//   reachability only when `allowSharedKeyAccess:false` — NOT the default install,
//                where the shared-key rung above returns first
//   coverage     each caller is invoked from a bash retry loop that heartbeats
//                between attempts, so the container is not silent for its whole
//                life — only for this stretch of it
//
// If either stretch grows past a minute it stops being coverable from outside and
// needs a channel of its own. Neither has, and that is the measurement.
import { TableClient, AzureNamedKeyCredential, type TransactionAction } from "@azure/data-tables";
import type { TokenCredential } from "@azure/identity";
import { TABLE } from "@m8t-stack/platform-release";
import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";
import { getCallerObjectId } from "./auth.js";
import { grantStorageTableDataContributor } from "./rbac.js";
import { discoverStampStorage } from "./platform-storage-discovery.js";

/** The slice of `TableClient` this module uses — the seam the unit tests fake. */
export interface TableLike {
  createTable(): Promise<unknown>;
  upsertEntity(entity: Record<string, unknown>, mode: "Replace"): Promise<unknown>;
  getEntity(partitionKey: string, rowKey: string): Promise<Record<string, unknown>>;
}

/** One concrete client must perform both ETag reads and the atomic transaction. */
export interface MetadataTransactionLike {
  getEntity(partitionKey: string, rowKey: string): Promise<Record<string, unknown>>;
  submitTransaction(actions: TransactionAction[]): Promise<unknown>;
}

export function is403(e: unknown): boolean {
  return (e as { statusCode?: number }).statusCode === 403;
}
export function is404(e: unknown): boolean {
  return (e as { statusCode?: number }).statusCode === 404;
}
function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Resolve a shared-key `TableClient` for the Metadata table (Owner can always list keys). */
export async function makeSharedKeyClient(opts: {
  tableEndpoint: string;
  accountResourceId: string;
  accountName: string;
}): Promise<TableLike | null> {
  // `az storage account keys list` requires --account-name + -g (it rejects --ids).
  const rg = /\/resourceGroups\/([^/]+)\//i.exec(opts.accountResourceId)?.[1];
  if (!rg) return null;
  const key = (
    await runAz(["storage", "account", "keys", "list", "--account-name", opts.accountName, "-g", rg, "--query", "[0].value", "-o", "tsv"])
  ).trim();
  if (!key) return null;
  return new TableClient(opts.tableEndpoint, TABLE, new AzureNamedKeyCredential(opts.accountName, key));
}

/** Resolve the real shared-key client when callers need transaction support. */
export async function makeSharedKeyTransactionClient(opts: {
  tableEndpoint: string;
  accountResourceId: string;
  accountName: string;
}): Promise<MetadataTransactionLike | null> {
  return (await makeSharedKeyClient(opts)) as MetadataTransactionLike | null;
}

export interface OpenMetadataTransactionOpts {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  label: string;
  errorCode: string;
  retryHint: string;
  onProgress?: (m: string) => void;
  // Test seams.
  discoverImpl?: typeof discoverStampStorage;
  aadClientImpl?: (tableEndpoint: string, credential: TokenCredential) => MetadataTransactionLike;
  sharedClientImpl?: (o: { tableEndpoint: string; accountResourceId: string; accountName: string }) => Promise<MetadataTransactionLike | null>;
  sleepImpl?: (ms: number) => Promise<void>;
}

/**
 * Open one transaction-capable Metadata client through the same auth ladder as
 * normal row writers. The read probe proves data-plane access; a 404 proves
 * access too and is returned to the caller to classify as an absent stamp.
 */
export async function openMetadataTransactionClient(opts: OpenMetadataTransactionOpts): Promise<MetadataTransactionLike> {
  const discover = opts.discoverImpl ?? discoverStampStorage;
  const makeAad = opts.aadClientImpl ?? ((endpoint: string, credential: TokenCredential): MetadataTransactionLike =>
    new TableClient(endpoint, TABLE, credential));
  const makeShared = opts.sharedClientImpl ?? makeSharedKeyTransactionClient;
  const nap = opts.sleepImpl ?? sleep;
  const storage = await discover({ credential: opts.credential, subscriptionId: opts.subscriptionId, resourceGroup: opts.resourceGroup });
  const probe = async (client: MetadataTransactionLike): Promise<"ok" | "forbidden"> => {
    try {
      await client.getEntity("system", "platform");
      return "ok";
    } catch (e) {
      if (is404(e)) return "ok";
      if (is403(e)) return "forbidden";
      throw e;
    }
  };

  const aad = makeAad(storage.tableEndpoint, opts.credential);
  if ((await probe(aad)) === "ok") return aad;

  opts.onProgress?.(`opening the ${opts.label} with the account key — your identity lacks the Storage Table data role.`);
  const shared = await makeShared(storage);
  if (shared) {
    if ((await probe(shared)) === "ok") return shared;
    // `allowSharedKeyAccess:false` still permits listing the account keys, but
    // the data plane rejects the resulting credential with 403. That is the
    // signal to continue to the bounded self-grant rung, not a terminal error.
  }

  opts.onProgress?.("shared-key access is disabled — granting Storage Table Data Contributor to your identity (one-time)…");
  try {
    const oid = await getCallerObjectId();
    await grantStorageTableDataContributor({
      credential: opts.credential,
      subscriptionId: opts.subscriptionId,
      scope: storage.accountResourceId,
      principalId: oid,
    });
  } catch (e) {
    throw new LocalCliError({
      code: opts.errorCode,
      message: `Cannot open the ${opts.label}: AAD lacks the Storage Table data role, shared-key access is disabled, and self-granting the role failed.`,
      hint: "Grant yourself 'Storage Table Data Contributor' on the storage account and retry.",
      cause: e,
    });
  }
  for (let i = 0; i < 6; i += 1) {
    await nap(10_000);
    if ((await probe(aad)) === "ok") return aad;
  }
  throw new LocalCliError({
    code: opts.errorCode,
    message: `Cannot open the ${opts.label}: the granted Storage Table data role has not propagated yet.`,
    hint: opts.retryHint,
  });
}

export interface UpsertMetadataOpts {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  entity: Record<string, unknown> & { partitionKey: string; rowKey: string };
  /** Human label for progress + error copy, e.g. "platform stamp" / "infra-params row". */
  label: string;
  /** `LocalCliError.code` used for both terminal failures. */
  errorCode: string;
  /**
   * `LocalCliError.hint` for the rung-4 (propagation-timeout) terminal
   * failure. Required rather than defaulted: the right remedy depends on
   * what row the caller is writing (the stamp caller points at
   * `m8t platform update`; a future writer may need different guidance), and
   * a generic default risks silently shipping the wrong — or no — remedy for
   * a new caller, which is the exact regression this field exists to prevent.
   */
  retryHint: string;
  onProgress?: (m: string) => void;
  // Test seams — production callers pass none of these.
  discoverImpl?: typeof discoverStampStorage;
  aadClientImpl?: (tableEndpoint: string, credential: TokenCredential) => TableLike;
  sharedClientImpl?: (o: { tableEndpoint: string; accountResourceId: string; accountName: string }) => Promise<TableLike | null>;
  sleepImpl?: (ms: number) => Promise<void>;
}

/**
 * Write one Metadata-table row via a 4-step auth ladder. The probe IS the real
 * write (`upsertEntity`) — `createTable()` succeeding is not evidence of
 * entity-write access (a founder who is subscription Owner but lacks
 * "Storage Table Data Contributor" can create/see a table that already
 * exists, then 403 on the actual upsert).
 *
 *   1. AAD write — try the caller's own credential directly. An identity that
 *      already holds the data-plane role (e.g. the updater MI) stops here.
 *   2. Shared-key fallback — on a 403, fall back to the account key (fast +
 *      reliable; an Owner can always list keys). One-line progress warning.
 *   3. Self-grant + bounded backoff — only if shared key is unavailable
 *      (`allowSharedKeyAccess:false`): grant the caller Storage Table Data
 *      Contributor at account scope and retry the AAD write a few times
 *      (data-plane role propagation takes minutes).
 *   4. Still failing → throw `opts.errorCode`.
 *
 * A non-403 error at step 1 rethrows immediately — it's a real failure, not
 * part of the access-denied path this ladder exists to handle.
 */
export async function upsertMetadataEntity(opts: UpsertMetadataOpts): Promise<void> {
  const discover = opts.discoverImpl ?? discoverStampStorage;
  const makeAad =
    opts.aadClientImpl ?? ((endpoint: string, credential: TokenCredential): TableLike => new TableClient(endpoint, TABLE, credential));
  const makeShared = opts.sharedClientImpl ?? makeSharedKeyClient;
  const nap = opts.sleepImpl ?? sleep;

  const { tableEndpoint, accountResourceId, accountName } = await discover({
    credential: opts.credential,
    subscriptionId: opts.subscriptionId,
    resourceGroup: opts.resourceGroup,
  });

  // 1. AAD write.
  const aad = makeAad(tableEndpoint, opts.credential);
  await aad.createTable().catch(() => undefined); // best-effort ensure; not a proof of write-access
  try {
    await aad.upsertEntity(opts.entity, "Replace");
    return;
  } catch (e) {
    if (!is403(e)) throw e;
  }

  // 2. Shared-key fallback (fast + reliable — Owner can always list keys).
  opts.onProgress?.(`writing the ${opts.label} with the account key — your identity lacks the Storage Table data role.`);
  const shared = await makeShared({ tableEndpoint, accountResourceId, accountName });
  if (shared) {
    await shared.createTable().catch(() => undefined);
    await shared.upsertEntity(opts.entity, "Replace");
    return;
  }

  // 3. Self-grant (last resort — shared-key is disabled). A grant failure (no
  //    OID, or the caller lacks role-assignment rights) is terminal.
  opts.onProgress?.("shared-key access is disabled — granting Storage Table Data Contributor to your identity (one-time)…");
  try {
    const oid = await getCallerObjectId();
    await grantStorageTableDataContributor({
      credential: opts.credential,
      subscriptionId: opts.subscriptionId,
      scope: accountResourceId,
      principalId: oid,
    });
  } catch (e) {
    throw new LocalCliError({
      code: opts.errorCode,
      message: `Cannot write the ${opts.label}: AAD lacks the Storage Table data role for ${accountName}, shared-key access is disabled, and self-granting the role failed.`,
      hint: "Grant yourself 'Storage Table Data Contributor' on the storage account and retry.",
      cause: e,
    });
  }

  // Grant succeeded — retry while the data-plane role propagates (minutes).
  // A non-403 error here is a genuine write failure → propagate it.
  for (let i = 0; i < 6; i++) {
    await nap(10_000);
    try {
      await aad.upsertEntity(opts.entity, "Replace");
      return;
    } catch (e) {
      if (!is403(e)) throw e;
    }
  }

  // 4. Granted, but the role has not propagated in time.
  throw new LocalCliError({
    code: opts.errorCode,
    message: `Cannot write the ${opts.label}: granted 'Storage Table Data Contributor' on ${accountName}, but the data-plane role has not propagated yet.`,
    hint: opts.retryHint,
  });
}
